/* MetabolicPathwaysCard — показва активираните метаболитни пътища
 * от пациентския профил (iter421.6). Pulls /metabolic-pathways.
 *
 * Тон: "Активна — заслужава проследяване" / "Заслужава внимание".
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Network, ChevronDown, ChevronUp, Leaf, Hand, Info, Loader2, Sparkles } from "lucide-react";
import axios from "axios";

function PathwayRow({ pw, isDark, bg }) {
  const [open, setOpen] = useState(false);
  const isActive = pw.status_bg.includes("Активна");
  return (
    <div className={`rounded-xl border ${
          isActive
            ? (isDark ? "border-violet-500/30 bg-violet-500/5" : "border-violet-200 bg-violet-50/40")
            : (isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white")
        }`} data-testid={`pathway-${pw.id}`}>
      <button onClick={() => setOpen((o) => !o)}
              className="w-full p-3 flex items-center gap-3 text-left">
        <div className={`w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${
              isActive ? "from-violet-500 to-fuchsia-500" : "from-slate-400 to-slate-500"
            } text-white`}>
          <Network className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? pw.name_bg : (pw.name_en || pw.name_bg)}
          </p>
          <p className={`text-[10.5px] font-semibold ${
                isActive ? (isDark ? "text-violet-300" : "text-violet-700") : (isDark ? "text-slate-400" : "text-slate-500")
              }`}>
            {pw.status_bg} · {pw.triggers_bg.length} {bg ? "тригера" : "triggers"}
          </p>
        </div>
        {open ? <ChevronUp className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
              : <ChevronDown className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />}
      </button>
      {open && (
        <div className={`px-3 pb-3 space-y-2.5 border-t ${isDark ? "border-slate-800" : "border-slate-200"} pt-2.5`}>
          <p className={`text-[11.5px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {pw.description_bg}
          </p>
          <div className="flex flex-wrap gap-1">
            {pw.triggers_bg.map((t, i) => (
              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                }`}>{t}</span>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                <Leaf className="w-3 h-3" /> {bg ? "Подкрепят пътя" : "Supportive"}
              </p>
              <ul className={`text-[10.5px] space-y-0.5 ${isDark ? "text-emerald-200/90" : "text-emerald-900/80"}`}>
                {pw.supportive_nutrients_bg.slice(0, 6).map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                <Hand className="w-3 h-3" /> {bg ? "Ограничете" : "Limit"}
              </p>
              <ul className={`text-[10.5px] space-y-0.5 ${isDark ? "text-amber-200/90" : "text-amber-900/80"}`}>
                {pw.limit_nutrients_bg.slice(0, 6).map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            </div>
          </div>
          {/* iter423 — Mind-body practices per pathway */}
          {Array.isArray(pw.practices_bg) && pw.practices_bg.length > 0 && (
            <div className={`mt-2 p-2.5 rounded-lg border ${
              isDark ? "border-fuchsia-500/30 bg-fuchsia-500/5" : "border-fuchsia-200 bg-fuchsia-50/40"
            }`} data-testid={`pathway-practices-${pw.id}`}>
              <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1.5 ${isDark ? "text-fuchsia-300" : "text-fuchsia-700"}`}>
                <Sparkles className="w-3 h-3" /> {bg ? "Тяло-ум практики" : "Mind-body practices"}
              </p>
              <div className="space-y-1.5">
                {pw.practices_bg.map((p, i) => (
                  <div key={i} className="text-[10.5px] leading-relaxed">
                    <span className={`font-bold ${isDark ? "text-fuchsia-100" : "text-fuchsia-900"}`}>{p.name}</span>
                    {p.frequency && (
                      <span className={`ml-1 text-[10px] ${isDark ? "text-fuchsia-300/80" : "text-fuchsia-700/80"}`}>
                        — {p.frequency}
                      </span>
                    )}
                    {p.rationale && (
                      <p className={`text-[10px] italic mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                        {p.rationale}{p.citation && <span className={isDark ? "text-fuchsia-400/70" : "text-fuchsia-700/70"}> · {p.citation}</span>}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <p className={`text-[9px] italic mt-1.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? "Образователни препоръки от рецензирана литература. Винаги обсъждайте с лекаря си." : "Educational recommendations from peer-reviewed literature."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MetabolicPathwaysCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/health-intelligence/metabolic-pathways`);
      setData(r.data);
    } catch (_e) {}
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className={`w-5 h-5 animate-spin ${isDark ? "text-violet-400" : "text-violet-600"}`} />
      </div>
    );
  }
  if (!data) return null;
  const pathways = data.pathways || [];

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}
          data-testid="metabolic-pathways-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex-shrink-0">
            <Network className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Активни метаболитни пътища" : "Active metabolic pathways"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? `Извлечени от Вашите състояния (${data.input_summary?.conditions_count || 0}) и фамилна анамнеза (${data.input_summary?.family_history_count || 0}).`
                : `Derived from your conditions (${data.input_summary?.conditions_count || 0}) and family history (${data.input_summary?.family_history_count || 0}).`}
            </p>
          </div>
          {data.total_active > 0 && (
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-100 text-violet-700"
              }`}>
              {data.total_active} {bg ? "активни" : "active"}
            </span>
          )}
        </div>

        {pathways.length === 0 ? (
          <div className={`p-4 rounded-xl border-dashed border-2 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-slate-200 bg-slate-50"}`}>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Все още няма данни. Добавете заболявания или фамилна анамнеза в Personal Health Manager, за да видим активните метаболитни пътища."
                : "No data yet. Add conditions or family history in Personal Health Manager to see active pathways."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {pathways.map((pw) => <PathwayRow key={pw.id} pw={pw} isDark={isDark} bg={bg} />)}
          </div>
        )}

        <p className={`text-[10px] italic flex items-start gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>{bg ? `${data.method_bg} ${data.disclaimer_bg}` : "Educational rule-based mapping — not a diagnosis."}</span>
        </p>
      </CardContent>
    </Card>
  );
}
